import Markdown from 'react-markdown';
import { cn } from '@/lib/utils/cn';
import type { ComponentPropsWithoutRef } from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/\*\*/g, '')
    .replace(/[^a-z0-9áéíóúñ]+/g, '-')
    .replace(/^-|-$/g, '');
}

function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractText((children as { props: { children?: React.ReactNode } }).props.children);
  }
  return '';
}

function HeadingWithId({
  level,
  children,
  ...props
}: ComponentPropsWithoutRef<'h2'> & { level: number }) {
  const Tag = `h${level}` as 'h2' | 'h3';
  const text = extractText(children);
  const id = generateSlug(text);
  return (
    <Tag id={id} {...props}>
      {children}
    </Tag>
  );
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        'prose prose-slate max-w-none dark:prose-invert prose-headings:scroll-mt-20 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-lg prose-h3:font-semibold prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-table:text-sm prose-th:text-left prose-th:font-semibold prose-th:py-2 prose-th:px-3 prose-td:py-2 prose-td:px-3 prose-blockquote:border-primary/30 prose-blockquote:text-muted-foreground',
        className
      )}
    >
      <Markdown
        components={{
          h2: (props) => <HeadingWithId level={2} {...props} />,
          h3: (props) => <HeadingWithId level={3} {...props} />,
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}
